import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout from './LegalPageLayout';

const returnEligibility = [
  {
    title: 'Incorrect design, style, or fabric',
    copy: 'The delivered garment does not match the design, style, customization options, or fabric selected and approved by you at the time of order.',
  },
  {
    title: 'Damaged or defective product',
    copy: 'The garment is received in damaged condition or contains a manufacturing defect.',
  },
  {
    title: 'Delivery delay',
    copy: 'Your order is delivered more than five (5) days after the confirmed delivery date, and the delay is solely attributable to Maviinci.',
  },
  {
    title: 'Fit issues after three alterations',
    copy: 'The garment still does not fit properly even after three complimentary alterations provided by Maviinci.',
  },
];

const nonReturnableCases = [
  'A fit issue is reported more than 15 days after delivery, or damage or a wrong item more than 48 hours after delivery.',
  'The garment has been used, washed or damaged after delivery, or altered by anyone other than Maviinci (an alteration by your own tailor that we approved through your order page is fine).',
  'The product was made exactly according to your approved design, fabric, and specifications.',
  'Incorrect measurements were provided by the customer or a third party.',
  'Minor color differences caused by screen display variations.',
  'Standard tailoring tolerance of up to 0.5 inch (1.27 cm).',
  'Requests based on change of mind, personal preference, or occasion cancellation.',
  'Delivery delays caused by courier partners, natural events, government restrictions, customer unavailability, or other factors beyond Maviinci\'s control.',
];

export default function ReturnsPage() {
  return (
    <LegalPageLayout
      title="Return & Refund Policy"
      subtitle="How returns, complimentary alterations, and refunds are handled for custom-made Maviinci garments."
      lastUpdated="May 16, 2026"
      activePath="/returns"
    >
      <div className="legal-highlight-card">
        <p>
          At <strong>Maviinci</strong>, every garment is custom-made according to your selected
          design, fabric, measurements, and preferences. We are committed to delivering
          high-quality tailored clothing and ensuring customer satisfaction. This Return & Refund
          Policy explains the conditions under which returns, alterations, and refunds are accepted.
        </p>
      </div>

      <h2>1. Eligibility for Returns and Refunds</h2>
      <p>
        Because Maviinci garments are made to order, returns and refunds are accepted only in the
        specific circumstances listed below.
      </p>

      <div className="legal-summary-grid">
        {returnEligibility.map((item) => (
          <article key={item.title} className="legal-summary-card">
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
          </article>
        ))}
      </div>

      <p>
        Delivery-delay refunds apply only when the delay is solely attributable to Maviinci.
        Delays caused by courier partners, natural events, government restrictions, or customer
        unavailability are not eligible for refund.
      </p>

      <h2>2. Complimentary Alterations</h2>
      <p>
        Maviinci provides up to <strong>three (3) complimentary alterations on each piece</strong>,
        requested within <strong>15 days of delivery</strong>, to achieve the best possible fit. Ask from
        your order page: we either collect the piece and alter it at our atelier at no cost, or you
        have it altered by a tailor near you and we pay the bill back to your UPI, up to ₹500 for a
        coat, ₹300 for a jacket and ₹200 for a pant, shirt, kurta, pajama or vest.
      </p>
      <ul>
        <li>Alteration requests are subject to garment feasibility.</li>
        <li>Alterations must be based on the original approved specifications.</li>
        <li>Complimentary alterations are meant to improve fit, not to redesign the garment.</li>
        <li>
          A piece that arrives dirty, damaged or wrong is reported from the same order page within
          48 hours. It is our fault, so it does not use one of your three alterations: we collect it
          free and clean it, repair it, make it again or refund it.
        </li>
        <li>
          When an altered piece comes back from our atelier, the windows start again from that day:
          48 hours for damage and 15 days for the fit.
        </li>
      </ul>

      <h2>3. Customer Measurement Responsibility</h2>
      <p>
        Customers are responsible for submitting accurate measurements. Measurement responsibility
        depends on how the measurements were collected.
      </p>
      <ul>
        <li>
          If measurements are generated using Maviinci's measurement tools or provided by an
          authorized Maviinci representative, Maviinci will take responsibility for fit accuracy.
        </li>
        <li>
          If measurements are entered manually by the customer or supplied by a third party,
          Maviinci will provide alteration support, but refunds may not be applicable for fit
          issues caused by inaccurate measurements.
        </li>
      </ul>

      <h2>4. Return Request Timeline</h2>
      <p>
        A <strong>fit issue</strong> must be reported within <strong>15 days</strong> of receiving the
        garment. A <strong>wrong item, manufacturing defect or damage</strong> must be reported within
        <strong>48 hours</strong> of delivery, or within 48 hours of receiving an altered garment.
      </p>
      <p>Please provide the following details when raising a request:</p>
      <ul>
        <li>Order number</li>
        <li>Photos or videos clearly showing the issue</li>
        <li>A brief description of the concern</li>
      </ul>

      <h2>5. Refund Process</h2>
      <p>If your claim is approved:</p>
      <ul>
        <li>The refund will be issued to the original payment method.</li>
        <li>A 2% payment processing fee will be deducted from the refund amount.</li>
        <li>Refunds are generally processed within 5-7 business days after approval.</li>
      </ul>

      <h2>6. Non-Returnable and Non-Refundable Cases</h2>
      <p>Returns or refunds are not applicable in the following situations:</p>
      <ul>
        {nonReturnableCases.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>

      <h2>7. Return Shipping Costs</h2>
      <p>
        If the issue is due to Maviinci's error, such as wrong item, wrong fabric, manufacturing
        defect, or damage, Maviinci will bear the return shipping charges.
      </p>
      <p>
        In all other approved cases, return shipping costs must be borne by the customer.
      </p>

      <h2>8. Order Approval and Finality</h2>
      <p>By placing an order, you confirm that you have reviewed and approved:</p>
      <ul>
        <li>Design and customization details</li>
        <li>Fabric selection</li>
        <li>Measurements</li>
        <li>Pricing</li>
        <li>Estimated delivery date</li>
      </ul>
      <p>
        Once production begins, changes or cancellations may not be possible.
      </p>

      <h2>9. Right to Inspect and Reject Claims</h2>
      <p>
        Maviinci reserves the right to inspect the returned garment and supporting evidence before
        approving any return, alteration, or refund request. Claims that do not meet the conditions
        of this policy may be rejected.
      </p>

      <h2>10. Policy Updates</h2>
      <p>
        Maviinci reserves the right to amend this Return & Refund Policy at any time without prior
        notice. Updated versions will be effective immediately upon publication on our website.
      </p>

      <h2>11. Contact Us</h2>
      <div className="legal-info-card">
        <div className="legal-info-row">
          <span className="legal-info-label">Support</span>
          <span className="legal-info-value">Maviinci Customer Support</span>
        </div>
        <div className="legal-info-row">
          <span className="legal-info-label">Email</span>
          <span className="legal-info-value">connect@bluepearlindustries.com</span>
        </div>
        <div className="legal-info-row">
          <span className="legal-info-label">Phone/WhatsApp</span>
          <span className="legal-info-value">+91-8871081395</span>
        </div>
        <div className="legal-info-row">
          <span className="legal-info-label">Website</span>
          <span className="legal-info-value">https://www.maviinci.in</span>
        </div>
      </div>

      <div className="legal-other-policies">
        <h3>Related Policies</h3>
        <div className="legal-other-policies-grid">
          <Link to="/terms" className="legal-policy-chip">Terms & Conditions</Link>
          <Link to="/privacy" className="legal-policy-chip">Privacy Policy</Link>
          <Link to="/shipping" className="legal-policy-chip">Shipping & Delivery</Link>
          <Link to="/cancellation" className="legal-policy-chip">Cancellation Policy</Link>
        </div>
      </div>
    </LegalPageLayout>
  );
}
